// src/components/LicenseBanner.js — LIC-106
//
// Persistent banner överst i Layout som synliggör backend-licens-state
// när den behöver användarens uppmärksamhet:
//   - expired_grace  → orange varning, X dagar kvar i grace
//   - expired_locked → röd, skrivoperationer låsta
//   - invalid        → röd, signatur ogiltig (admin behöver ladda upp ny)
//   - missing        → blå info, bara för admins (annars dölj — irrelevant)
//
// Skipas vid valid och under loading. Banner är persistent (visas på alla
// sidor) men dismissable per session via localStorage-flagga.

import React, { useState, useCallback } from 'react';
import { FaExclamationTriangle, FaTimesCircle, FaInfoCircle, FaTimes, FaUpload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { t, useLanguage } from '../useTranslations';
import { useLicense } from '../hooks/useLicense';
import usePermission from '../hooks/usePermission';
import './LicenseBanner.css';

const DISMISS_KEY = 'rms_license_banner_dismissed_state';

const VARIANT = {
  expired_grace: { className: 'license-banner--warn', icon: FaExclamationTriangle },
  expired_locked: { className: 'license-banner--err', icon: FaTimesCircle },
  invalid: { className: 'license-banner--err', icon: FaTimesCircle },
  missing: { className: 'license-banner--info', icon: FaInfoCircle },
};

const LicenseBanner = () => {
  useLanguage();
  const navigate = useNavigate();
  const { canAdmin } = usePermission('Admin');
  const { state, daysSinceExpiry, customer, loading } = useLicense();

  const dismissedState = (typeof window !== 'undefined') ? localStorage.getItem(DISMISS_KEY) : null;
  const [dismissed, setDismissed] = useState(dismissedState === state);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, state);
    setDismissed(true);
  }, [state]);

  if (loading) return null;
  if (state === 'valid' || state === 'unknown' || state === 'not_initialized') return null;

  // 'missing' visas bara för admins (vanlig user kan inget göra åt det)
  if (state === 'missing' && !canAdmin) return null;
  if (dismissed) return null;

  const variant = VARIANT[state] || VARIANT.missing;
  const Icon = variant.icon;

  let messageKey = `licenseBanner.message.${state}`;
  let messageVars = {};
  if (state === 'expired_grace') {
    messageVars = {
      customer: customer || t('licenseUpload.field.customer'),
      daysSince: daysSinceExpiry ?? '?',
      daysLeft: Math.max(0, 30 - (daysSinceExpiry ?? 30)),
    };
  } else if (state === 'expired_locked') {
    messageVars = {
      customer: customer || '',
      daysSince: daysSinceExpiry ?? '?',
    };
  }

  return (
    <div className={`license-banner ${variant.className}`} role="status">
      <Icon className="license-banner-icon" />
      <span className="license-banner-message">
        {t(messageKey, messageVars)}
      </span>
      {canAdmin && (
        <button
          type="button"
          className="license-banner-action"
          onClick={() => navigate('/admin?tab=usersAccess&tool=licenseUpload')}
        >
          <FaUpload /> {t('licenseBanner.upload')}
        </button>
      )}
      <button
        type="button"
        className="license-banner-dismiss"
        onClick={handleDismiss}
        aria-label={t('licenseBanner.dismiss')}
        title={t('licenseBanner.dismiss')}
      >
        <FaTimes />
      </button>
    </div>
  );
};

export default LicenseBanner;
